import Media from '@/components/shared/Media/Media';
import { ArrowRight } from '@1debit/icons';
import RichTextHeadline from 'components/shared/RichTextHeadline/RichTextHeadline';
import { RelatedArticleProps } from './types';
import Link from 'components/shared/Link/Link';
import { ArticleTag } from './ArticleTag';
import { getRichTextValue, formatDate } from 'utils/formatters';
import { liveUpdateAttrs } from 'libs/contentful/utils';
import { cn, getColorway } from 'utils/styles';
import { Colorways } from 'types/constants';

export const MediumArticleDisplayVariant = {
  Default: 'default',
  Card: 'card',
} as const;
export type MediumArticleDisplayVariant =
  (typeof MediumArticleDisplayVariant)[keyof typeof MediumArticleDisplayVariant];

const DISPLAY_VARIANT_STYLES = {
  [MediumArticleDisplayVariant.Default]: {
    container: '',
    text: '',
    image: '',
  },
  [MediumArticleDisplayVariant.Card]: {
    container:
      'items-start rounded-lg overflow-hidden group no-underline transition-colors duration-200 hover:bg-[rgb(var(--chime-green-dark))] hover:text-content-inverse focus-visible:bg-[rgb(var(--chime-green-dark))] focus-visible:text-content-inverse in-cw-dark-colorways:hover:bg-[rgb(var(--chime-green))] in-cw-dark-colorways:focus-visible:bg-[rgb(var(--chime-green))]',
    text: 'p-6',
    image: 'rounded-t-lg rounded-b-none',
  },
} as const;

interface MediumArticleProps extends RelatedArticleProps {
  displayVariant?: MediumArticleDisplayVariant;
  colorway?: Colorways;
  showDates?: boolean;
  includeCategoryLinks?: boolean;
  publishDate?: string;
  className?: string;
}

const MediumArticle = ({
  articleImage,
  articleTitle,
  category,
  slug,
  href,
  sys,
  publishDate,
  showDates,
  includeCategoryLinks,
  displayVariant = MediumArticleDisplayVariant.Default,
  colorway,
  className,
}: MediumArticleProps) => {
  const categoryTitle = category?.label;
  const linkText = 'Read more';
  const plainTitle = getRichTextValue(articleTitle);
  let formattedDate;
  if (publishDate) {
    formattedDate = formatDate(publishDate);
  } else if (sys?.createdAt) {
    formattedDate = formatDate(sys?.createdAt);
  }

  const variantStyles = DISPLAY_VARIANT_STYLES[displayVariant];
  const isCard = displayVariant === MediumArticleDisplayVariant.Card;

  const Wrapper = isCard ? Link : 'div';
  const internalPage = href ? undefined : { slug };
  const wrapperProps = isCard ? { internalPage, href, 'aria-label': plainTitle } : {};

  return (
    <Wrapper
      {...wrapperProps}
      data-testid={'mediumArticle'}
      className={cn(
        'flex flex-1 flex-col gap-y-6',
        colorway && getColorway(colorway),
        variantStyles.container,
        className,
      )}>
      {articleImage && (
        <div className="h-50 sm:h-64 w-full" {...liveUpdateAttrs(sys?.id, 'articleImage')}>
          <Media
            media={articleImage}
            sizes={'(max-width: 750px) 100vw, 420px'}
            className={cn(variantStyles.image, 'object-cover h-full w-full')}
          />
        </div>
      )}
      <div className={cn(variantStyles.text, 'flex flex-col flex-1 space-y-2.5')}>
        {categoryTitle && (
          <ArticleTag label={categoryTitle} href={includeCategoryLinks && !isCard ? category?.url : undefined} />
        )}
        <div className="font-sans space-y-8">
          <div className="text-heading-sm" {...liveUpdateAttrs(sys?.id, 'articleTitle')}>
            {isCard ? (
              <RichTextHeadline level="h3" headline={articleTitle} />
            ) : (
              <Link internalPage={internalPage} href={href}>
                <RichTextHeadline level="h3" headline={articleTitle} />
              </Link>
            )}
          </div>
          {showDates && formattedDate && (
            <div data-testid={'mediumArticleDate'} className="font-sans font-medium text-base -mt-5">
              <p>{formattedDate}</p>
            </div>
          )}
        </div>
        <div className="text-label-saans mt-auto">
          {isCard ? (
            <>
              {linkText} <ArrowRight className={'inline size-(--ck-icon-size)'} />
            </>
          ) : (
            <Link
              internalPage={internalPage}
              href={href}
              className={'hover:underline'}
              aria-label={`${linkText}: ${plainTitle}`}>
              {linkText} <ArrowRight className={'inline size-(--ck-icon-size)'} />
            </Link>
          )}
        </div>
      </div>
    </Wrapper>
  );
};

export default MediumArticle;
